import React, { ReactElement } from 'react';

import { Inject } from '@odinjs/odin';

import { Endpoint, FormViewService, ProviderAccreditationPendenciesService, ToastServiceProviderAccreditation } from '@philips-emr/hpms-provider-accreditation-commons';
import { HpmsCustomCardEvent, HpmsCustomCardMenuRenderEvent, HPMSOnCreateMenu, HPMSOnOptionClick } from '@philips-emr/hpms-provider-accreditation-components';
import { ButtonAPI, FetchAPI, FieldAPI, FormAPI, PanelAPI, ToastNotificationAPI, Token } from '@philips-emr/tws-components';
import { Type } from '@philips-emr/tws-constants';
import { Value } from '@philips-emr/tws-core';
import Controller, { OnAfterFetch, OnCancel, OnChange, OnDynamicContentRender, OnMount, OnSubmit } from '@philips-emr/tws-core/lib/client';
import { FetchEvent, MountEvent, RenderEvent, SubmitEvent } from '@philips-emr/tws-core/lib/component/types';
import { HttpService, MessageService } from '@philips-emr/tws-core/lib/services';
import './accreditation-token.css';
import { isEmptyArrayOrObject } from '@philips-emr/tws-utils';

import { ProviderAccreditationEquipmentsAliases as Aliases } from '../../codegen';
import BaseController from '../BaseController';

import { AccreditationSteps } from './provider-accreditation-controller';

const localValidatorSUS = 'islowerOrEqualTheOtherValue';

type MyData = {
  nrSequence?: number,
  ieEquipment?: string,
  dsEquipment?: string,
  ieEquipmentAvailableSUS?: string | boolean,
  qtEquipment?: number,
  qtEquipmentSUS?: number
};

type MyException = {
  body: string,
};

const OptionKey = {
  EDIT: 'edit',
  DELETE: 'delete',
};

@Controller({ fragment: 'provider-accreditation-equipments' })
export default class ProviderAccreditationEquipmentsController extends BaseController {

  @Inject('fetch-list')
  private fetchList: FetchAPI;

  @Inject('field-amount-equipment-use')
  private fieldAmountUse: FieldAPI;

  @Inject('field-card')
  private fieldCards: FieldAPI;

  @Inject('form-equipment')
  private formEquipment!: FormAPI;

  @Inject('panel-pendencies')
  private panelPendencies: PanelAPI;

  @Inject('toast-notification')
  private toast: ToastNotificationAPI;

  @Inject('save-action')
  private saveAction: ButtonAPI;

  private requiredFields: FieldAPI[] = [];

  @OnMount([
    Aliases.FIELD_EQUIPMENT,
    Aliases.FIELD_TYPE_EQUIPMENT,
    Aliases.FIELD_AMOUNT_EQUIPMENT,
    Aliases.FIELD_AMOUNT_EQUIPMENT_USE,
  ])
  onMountRequiredFields(event: MountEvent): void {
    this.requiredFields.push(event.target as FieldAPI);
  }

  @OnMount('layout-general-information')
  onRootLayoutMount(): void {
    const { nrSeqAccreditation, nrSeqTypeAccreditation } = this.historyService.getCurrentRouteParams();
    this.context.set({ nrSeqAccreditation, nrSeqTypeAccreditation });
    this.fetchList.forceFetch();
  }

  @OnMount('form-panel')
  onMountPanelForm(event: MountEvent): void {
    const api = event.target as PanelAPI;
    FormViewService.shouldRenderForm(api, AccreditationSteps.EQUIPMENTS);
  }

  @HPMSOnCreateMenu('my-data-card')
  onCreateCardMenu(event: HpmsCustomCardMenuRenderEvent<Record<string, Value>>): void {
    const { data, shouldRender } = event;
    const { editable } = data;

    if (editable == 'N') {
      shouldRender(false);
    }
  }

  @OnMount('form-equipment')
  onMountForm(event: MountEvent): void {
    const apiForm = event.target as FormAPI;
    apiForm.setRecord({ ieEquipmentAvailableSUS: false });

    this.fieldAmountUse.addLocalValidator(localValidatorSUS, (amountUse) => {
      const quantity = this.formEquipment.getRecord()['qtEquipment'];
      const valid = !quantity || (Number(amountUse ?? 0) <= Number(quantity ?? 0));

      return [
        {
          message: !valid ? 'pa.valid-amount-use' : '',
          name: localValidatorSUS,
          valid,
        },
      ];
    });
  }

  @OnChange('field-available-sus')
  onChangeAvailableSus(): void {
    this.saveAction.setEnabled(this.formEquipment.isValid());
  }

  @HPMSOnOptionClick('my-data-card')
  onOptionClick(event: HpmsCustomCardEvent<Record<string, Value>>): void {
    const { data, key } = event;
    const { nrSequence } = data as MyData;
    const action = {
      [OptionKey.EDIT]: () => this.editRecord(Number(nrSequence)),
      [OptionKey.DELETE]: () => this.deleteRecord(Number(nrSequence)),
    };

    if (action[key]) {
      action[key]();
    }
  }

  @OnDynamicContentRender('dynamic-field-available-sus')
  public onDynamicContentRenderField(event: RenderEvent): void {
    const { data, render } = event;
    const { ieEquipmentAvailableSUS } = data as MyData;

    if (ieEquipmentAvailableSUS === 'S') {
      render((): ReactElement => this.getToken());
    }
  }

  @OnMount('fetch-pendencies')
  onMountFetchPendencies(event: MountEvent): void {
    ProviderAccreditationPendenciesService.fetchCardPendencies(event, AccreditationSteps.EQUIPMENTS);
  }

  @OnAfterFetch('fetch-pendencies')
  onAfterFetch(event: FetchEvent): void {
    if (isEmptyArrayOrObject(event.data)) {
      this.panelPendencies.setVisible(false);
    } else {
      ProviderAccreditationPendenciesService.setMessageToastPendency(this.toast, event);
    }
  }

  private getToken(): ReactElement {
    return <div className="u-token-list-card">
      <Token value="pa.equipment-available-sus" type={Type.NOTICE} />
    </div>;
  }

  @OnCancel('form-equipment')
  onCancelForm(): void {
    this.formEquipment.setRecord({});
    this.showValidations(false);
  }

  @OnSubmit('form-equipment')
  onSubmitForm(event: SubmitEvent): void {
    const record = event.record as MyData;
    const { nrSeqUser, nrSeqAccreditation } = this.historyService.getCurrentRouteParams();

    void HttpService.post(Endpoint.POST.EQUIPMENTS, { nrSeqUser, nrSeqAccreditation }, { body: { ...record } })
      .then(() => {
        this.showValidations(false);
        this.formEquipment.setRecord({});
        this.fieldCards.setVisible(true);
        this.fetchList.forceFetch();
      }).catch(({ body }: MyException) => {
        ToastServiceProviderAccreditation.error(body);
      });
  }

  private fetchEquipments(nrSeqUser: any, nrSeqAccreditation: any, nrSequence: number): void {
    void HttpService.get<Record<string, Value>>(Endpoint.GET.EQUIPMENTS, { nrSeqUser, nrSeqAccreditation, nrSequence })
      .then((resp) => {
        const { body = {} } = resp;

        this.formEquipment.setData(body);
        this.formEquipment.setRecord(body);
        this.formEquipment.validate();
      });
  }

  private editRecord(nrSequence: number): void {
    const { nrSeqUser, nrSeqAccreditation } = this.historyService.getCurrentRouteParams();
    this.fetchEquipments(nrSeqUser, nrSeqAccreditation, nrSequence);
  }

  private async deleteRecord(nrSequence: number): Promise<void> {
    try {
      const { nrSeqUser, nrSeqAccreditation } = this.historyService.getCurrentRouteParams();
      await HttpService.delete(Endpoint.DELETE.EQUIPMENTS, { nrSeqUser, nrSeqAccreditation, nrSequence });
      this.formEquipment.setRecord({});
      this.fetchList.forceFetch();
    } catch (e) {
      const { body } = e as MyException;
      ToastServiceProviderAccreditation.error(MessageService.translate(body));
    }
  }

  private showValidations(validate: boolean): void {
    this.requiredFields.forEach(field => field.setState({ showValidations: validate }));
  }
}
